import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, FileText, Calendar, Clock, CheckCircle,
  Briefcase, TrendingUp, Search, MapPin, ChevronRight, ArrowUp,
  Activity, Settings, Lock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import AnimateHeader from '../../components/dashboard/AnimateHeader';
import ProfileUpdateModal from '../../components/modals/ProfileUpdateModal';
import PasswordUpdateModal from '../../components/modals/PasswordUpdateModal';
import ApplicationDetailModal from '../../components/modals/ApplicationDetailModal';
import jobService, { Job } from '../../services/jobService';

interface Application {
  id: string;
  jobId: string;
  jobTitle: string;
  department: string;
  status: 'interviewed' | 'reviewed' | 'accepted' | 'rejected' | 'ai_pending' | 'human_pending';
  appliedAt: string;
  interviewDate?: string;
  interviewLink?: string;
}

interface InterviewSession {
  id: string;
  jobId: string;
  jobTitle: string;
  department?: string; // Add optional department field
  status: 'in_progress' | 'completed' | 'abandoned' | 'cancelled';
  started_at: string;
  finished_at?: string;
  interviewUrl: string;
}

interface Profile {
  email: string;
  name: string;
  phone?: string;
  cvFileName?: string;
  joinedAt: string;
  title?: string;
  skills?: any;
  experience?: any;
  education?: any;
  location?: string;
  about?: string;
}

const CandidateDashboard: React.FC = () => {
  const { user, logout, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'overview' | 'jobs' | 'profile'>('overview');
  const [applications, setApplications] = useState<Application[]>([]);
  const [interviewSessions, setInterviewSessions] = useState<InterviewSession[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(Date.now());
  const [hasActiveInterviews, setHasActiveInterviews] = useState<boolean>(false);
  const [showJobResultModal, setShowJobResultModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Development mode detection
  const isDevelopmentMode = import.meta.env.VITE_APP_ENV === 'development' ||
                           import.meta.env.VITE_APP_ENV === 'staging' ||
                           import.meta.env.NODE_ENV === 'development';

  // Utility function for date formatting
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Smart refresh function with throttling and context-aware logic
  const shouldRefreshData = (): boolean => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTime;
    const minRefreshInterval = 30000; // 30 seconds minimum between refreshes

    // Don't refresh if we just refreshed recently
    if (timeSinceLastRefresh < minRefreshInterval) {
      return false;
    }

    // Only refresh if there are likely status changes to check
    if (hasActiveInterviews) {
      return true;
    }

    // Check if there are any pending applications that might change
    const hasPendingApplications = applications.some(app =>
      ['ai_pending', 'human_pending', 'interviewed'].includes(app.status)
    );

    if (hasPendingApplications) {
      return true;
    }

    // Check if there are in-progress interview sessions
    const hasInProgressSessions = interviewSessions.some(session =>
      session.status === 'in_progress'
    );

    if (hasInProgressSessions) {
      return true;
    }

    return false;
  };

  const smartRefreshData = () => {
    // Prevent refresh if already loading
    if (loading) {
      return;
    }

    if (shouldRefreshData()) {
      setLastRefreshTime(Date.now());
      loadDashboardData();
    }
  };


  // Redirect to login if not authenticated
  useEffect(() => {
    // Wait for auth loading to complete before checking authentication
    if (authLoading) {
      return;
    }
    
    if (!isAuthenticated) {
      navigate('/login', { replace: true, state: { from: '/dashboard' } });
      return;
    }
    loadDashboardData();
  }, [isAuthenticated, navigate, authLoading]);

  // Refresh data when navigating to dashboard (to pick up new applications, etc.)
  useEffect(() => {
    // Only reload if authenticated and not loading
    if (isAuthenticated && !authLoading) {
      // Check if this is a refresh from an interview completion
      if (location.state?.refreshFromInterview) {
        // Add a small delay to ensure backend has processed the interview completion
        setTimeout(() => {
          loadDashboardData();
        }, 2000); // 2 second delay

        // Clear the state to prevent repeated refreshes
        window.history.replaceState({}, document.title);
      } else {
        loadDashboardData();
      }
    }
  }, [location.pathname, location.state, isAuthenticated, authLoading]);

  // Smart auto-refresh when user returns to the dashboard tab (only when necessary)
  useEffect(() => {
    const handleFocus = () => {
      if (isAuthenticated && !authLoading && !loading) {
        smartRefreshData();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated && !authLoading && !loading) {
        // Small delay to ensure any backend updates have been processed
        setTimeout(() => {
          smartRefreshData();
        }, 1000);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, authLoading, loading, lastRefreshTime, hasActiveInterviews, applications, interviewSessions]);

  // Scroll to top when tab changes
  useEffect(() => {
    // Then add a small delay for smooth visual transition if needed
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, [activeTab]);

  // Show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    // Scroll to absolute top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


      const loadDashboardData = async () => {
  setLoading(true);
  try {
    // Load profile data
    try {
      const profileRes = await apiClient.get('/auth/me');

      const p = profileRes.data?.data || {};

      const parsedProfile: Profile = {
        email: p.email || user?.email || '',
        name: p.name || user?.name || '',
        phone: p.phone,
        joinedAt: p.created_at || new Date().toISOString(),
        title: p.title,
        skills: p.skills,
        experience: p.experience,
        education: p.education,
        location: p.location,
        about: p.about,
        cvFileName: p.cv_url ? 'CV Uploaded' : undefined,
      };

      setProfile(parsedProfile);
    } catch (err) {
      // Set fallback profile
      setProfile({
        email: user?.email || '',
        name: user?.name || 'User',
        joinedAt: new Date().toISOString(),
      });
    }

    // Load applications
    try {
      const applicationsRes = await apiClient.get('/auth/candidates/applications');
      const apps = applicationsRes.data?.applications || [];
      const loadedApplications: Application[] = apps.map((app: any) => ({
        id: app.id,
        jobId: app.jobId, // Fixed: was app.job_id, should be app.jobId to match backend response
        jobTitle: app.jobTitle,
        department: app.department ?? 'Unknown',
        status: app.status,
        appliedAt: app.appliedAt,
        interviewDate: app.interviewDate,
        interviewLink: app.interviewLink,
      }));
      setApplications(loadedApplications);
    } catch (err) {
      setApplications([]);
    }

    // Load interview sessions
    try {
      const interviewRes = await apiClient.get('/interviews/');
      const loadedSessions = interviewRes.data?.data?.sessions || interviewRes.data?.data || [];
      setInterviewSessions(loadedSessions);

      // Update hasActiveInterviews state based on loaded data
      const activeInterviewsExist = loadedSessions.some((session: any) =>
        session.status === 'in_progress' || session.status === 'scheduled'
      );
      setHasActiveInterviews(activeInterviewsExist);
    } catch (err) {
      setInterviewSessions([]);
      setHasActiveInterviews(false);
    }

    // Load open positions
    try {
      const jobsData = await jobService.getJobs({ limit: 10 });
      setJobs(jobsData.jobs || []);
    } catch (err) {
      setJobs([]);
    }
  } finally {
    setLoading(false);
  }
};

  const getStatusBadgeColor = (status: string) => {
    const colors: { [key: string]: string } = {
      // Active Interview States
      'In Progress': 'bg-gradient-to-r from-amber-500/85 to-amber-600/90',
      'Ready to Begin': 'bg-gradient-to-r from-blue-500/80 to-blue-600/85',
      'Ready': 'bg-gradient-to-r from-blue-400/80 to-blue-600/85',
      'Interview Available': 'bg-gradient-to-r from-blue-500/80 to-blue-600/85',
      'Not Started': 'bg-gradient-to-r from-blue-500/80 to-blue-600/85',

      // Completed Interview States
      'Interview Completed': 'bg-gradient-to-r from-green-500/80 to-green-600/85',
      'Completed': 'bg-gradient-to-r from-green-500/80 to-green-600/85',

      // Review States
      'Under Review': 'bg-gradient-to-r from-orange-400/80 to-orange-500/85',
      'Review in Progress': 'bg-gradient-to-r from-orange-500/80 to-orange-600/85',
      'Interview Process': 'bg-gradient-to-r from-indigo-500/80 to-indigo-600/85',
      'Interviews Ongoing': 'bg-gradient-to-r from-indigo-500/80 to-indigo-600/85',

      // Success States
      'Congratulations!': 'bg-gradient-to-r from-emerald-500/80 to-emerald-600/85',
      'Accepted': 'bg-gradient-to-r from-emerald-500/80 to-emerald-600/85',

      // Rejection States
      'Application Not Selected': 'bg-gradient-to-r from-red-500/80 to-red-600/85',
      'Not Selected': 'bg-gradient-to-r from-red-500/80 to-red-600/85',
      'Rejected': 'bg-gradient-to-r from-red-400/80 to-red-500/85',
      'Interview Incomplete': 'bg-gradient-to-r from-yellow-500/80 to-yellow-600/85',

      // General Application States
      'Applied': 'bg-gradient-to-r from-cyan-500/80 to-cyan-600/85',
      'Application Received': 'bg-gradient-to-r from-cyan-500/80 to-cyan-600/85',
      'Waiting': 'bg-gradient-to-r from-gray-500/80 to-gray-600/85',
      'Pending': 'bg-gradient-to-r from-gray-500/80 to-gray-600/85',
    };
    return colors[status] || 'bg-gradient-to-r from-gray-500/80 to-gray-600/85';
  };

  const getStatusBadgeGlow = (status: string) => {
    const colors: { [key: string]: string } = {
      // Active Interview States
      'In Progress': 'bg-gradient-to-r from-amber-500 to-amber-600',
      'Ready to Begin': 'bg-gradient-to-r from-blue-500 to-blue-600',
      'Ready': 'bg-gradient-to-r from-blue-500 to-blue-600',
      'Interview Available': 'bg-gradient-to-r from-blue-500 to-blue-600',
      'Not Started': 'bg-gradient-to-r from-blue-500 to-blue-600',

      // Completed Interview States
      'Interview Completed': 'bg-gradient-to-r from-green-500 to-green-600',
      'Completed': 'bg-gradient-to-r from-green-500 to-green-600',

      // Review States
      'Under Review': 'bg-gradient-to-r from-orange-500 to-orange-600',
      'Review in Progress': 'bg-gradient-to-r from-orange-500 to-orange-600',
      'Interview Process': 'bg-gradient-to-r from-indigo-500 to-indigo-600',
      'Interviews Ongoing': 'bg-gradient-to-r from-indigo-500 to-indigo-600',

      // Success States
      'Congratulations!': 'bg-gradient-to-r from-emerald-500 to-emerald-600',
      'Accepted': 'bg-gradient-to-r from-emerald-500 to-emerald-600',

      // Rejection States
      'Application Not Selected': 'bg-gradient-to-r from-red-500 to-red-600',
      'Not Selected': 'bg-gradient-to-r from-red-500 to-red-600',
      'Rejected': 'bg-gradient-to-r from-red-500 to-red-600',
      'Interview Incomplete': 'bg-gradient-to-r from-yellow-500 to-yellow-600',

      // General Application States
      'Applied': 'bg-gradient-to-r from-cyan-500 to-cyan-600',
      'Application Received': 'bg-gradient-to-r from-cyan-500 to-cyan-600',
      'Waiting': 'bg-gradient-to-r from-gray-500 to-gray-600',
      'Pending': 'bg-gradient-to-r from-gray-500 to-gray-600',
    };
    return colors[status] || 'bg-gradient-to-r from-gray-500 to-gray-600';
  };

  const getStatusBadgeBorder = (status: string) => {
    const colors: { [key: string]: string } = {
      // Active Interview States
      'In Progress': 'border border-amber-700/20',
      'Ready to Begin': 'border border-blue-700/20',
      'Ready': 'border border-blue-700/20',
      'Interview Available': 'border border-blue-700/20',
      'Not Started': 'border border-blue-700/20',

      // Completed Interview States
      'Interview Completed': 'border border-green-700/20',
      'Completed': 'border border-green-700/20',

      // Review States
      'Under Review': 'border border-orange-700/20',
      'Review in Progress': 'border border-orange-700/20',
      'Interview Process': 'border border-indigo-700/20',
      'Interviews Ongoing': 'border border-indigo-700/20',

      // Success States
      'Congratulations!': 'border border-emerald-700/20',
      'Accepted': 'border border-emerald-700/20',

      // Rejection States
      'Application Not Selected': 'border border-red-700/20',
      'Not Selected': 'border border-red-700/20',
      'Rejected': 'border border-red-700/20',
      'Interview Incomplete': 'border border-yellow-700/20',

      // General Application States
      'Applied': 'border border-cyan-700/20',
      'Application Received': 'border border-cyan-700/20',
      'Waiting': 'border border-gray-700/20',
      'Pending': 'border border-gray-700/20',
    };
    return colors[status] || 'border border-gray-700/20';
  };

  // Standardized status badge renderer - use this everywhere for consistency
  const renderStatusBadge = (
    statusInfo: { message: string; [key: string]: any },
    position: 'top-right' | 'bottom-right' = 'top-right'
  ) => {
    const positionClasses = position === 'bottom-right'
      ? 'absolute bottom-4 right-4 z-50'
      : 'absolute -top-2 -right-2 z-50';
      
    return (
      <div className={positionClasses}>
        <div className="relative">
          <div className={`absolute inset-0 ${getStatusBadgeGlow(statusInfo.message)} rounded-full blur opacity-30 group-hover:opacity-50 transition-opacity duration-300`} />
          <span className={`relative px-3 py-1 rounded-full text-[11px] font-bold ${getStatusBadgeColor(statusInfo.message)} text-white shadow-xl ${getStatusBadgeBorder(statusInfo.message)}`}>
            {statusInfo.message.toUpperCase()}
          </span>
        </div>
      </div>
    );
  };

  // Standardized date text formatter based on status and context
  const getStatusBasedDateText = (
    statusMessage: string, 
    appliedAt: string, 
    startedAt?: string, 
    completedAt?: string
  ): string => {
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    switch (statusMessage) {
      case 'In Progress':
        return startedAt ? `Started ${formatDate(startedAt)}` : `Applied ${formatDate(appliedAt)}`;

      case 'Ready':
      case 'Ready to Begin':
      case 'Interview Available':
      case 'Not Started':
        return `Applied ${formatDate(appliedAt)}`;
      
      case 'Completed':
      case 'Interview Completed':
        return completedAt ? `Completed ${formatDate(completedAt)}` : `Applied ${formatDate(appliedAt)}`;
      
      case 'Under Review':
      case 'Review in Progress':
      case 'Reviewing':
        return `Submitted ${formatDate(appliedAt)}`;
      
      case 'Accepted':
        return `Accepted ${formatDate(appliedAt)}`;
      
      case 'Rejected':
      case 'Not Selected':
        return `Closed ${formatDate(appliedAt)}`;
      
      case 'Applied':
      case 'Application Received':
        return `Applied ${formatDate(appliedAt)}`;
      
      case 'Waiting':
      case 'Pending':
        return `Since ${formatDate(appliedAt)}`;
      
      default:
        return `Applied ${formatDate(appliedAt)}`;
    }
  };

  // Standardized Application Card Component with text truncation
  const ApplicationCard = ({
    title,
    department,
    dateText,
    statusInfo,
    onClick,
    animationDelay = 0
  }: {
    title: string;
    department: string;
    dateText: string;
    statusInfo: { message: string; [key: string]: any };
    onClick: () => void;
    animationDelay?: number;
  }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: animationDelay * 0.1, type: "spring", stiffness: 100 }}
        onClick={onClick}
        whileHover={{ y: -2, transition: { duration: 0.2 } }}
        className="relative bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-5 shadow-xl hover:shadow-2xl hover:bg-white/[0.07] hover:border-white/20 transition-all duration-300 cursor-pointer group"
      >
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-orange-500/5" />
        </div>

        {/* Status Badge */}
        {renderStatusBadge(statusInfo, 'top-right')}

        <div className="relative z-10 flex items-center justify-between">
          <div className="flex-1 pr-8">
            <h3 className="text-xl font-semibold text-white mb-2 truncate" title={title}>
              {title}
            </h3>
            <div className="flex items-center gap-3 text-gray-400 text-sm">
              <span className="flex items-center gap-1.5 truncate">
                <Briefcase className="w-4 h-4 flex-shrink-0" />
                <span className="truncate" title={department}>{department}</span>
              </span>
              <span className="text-gray-500 flex-shrink-0">•</span>
              <span className="text-gray-500 truncate" title={dateText}>
                {dateText}
              </span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-400 transition-colors flex-shrink-0" />
        </div>
      </motion.div>
    );
  };

  // Clean Job Card Component with simple truncation
  const JobCard = ({ 
    job, 
    onClick, 
    animationDelay = 0 
  }: {
    job: Job;
    onClick: (job: Job) => void;
    animationDelay?: number;
  }) => {
    const jobStatus = getJobApplicationStatus(job.id);
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: animationDelay * 0.1 }}
        whileHover={{ 
          y: -8, 
          scale: 1.02,
          transition: { duration: 0.3, type: "spring", stiffness: 200 } 
        }}
        className="relative bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl p-4 shadow-lg hover:shadow-2xl hover:bg-white/[0.08] hover:border-white/25 transition-all duration-300 group cursor-pointer h-44"
        onClick={() => onClick(job)}
      >
        {/* Enhanced floating effect on hover */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-amber-500/8 to-orange-500/8 opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none" />

        <div className="relative flex flex-col h-full">
          {/* Header - Clean title with proper truncation */}
          <h3 className="text-lg font-semibold text-white mb-2 truncate min-h-[1.75rem]" title={job.title}>
            {job.title}
          </h3>

          {/* Department and Location - Improved layout */}
          <div className="flex items-center gap-3 text-sm text-gray-400 mb-3">
            {job.department && (
              <span className="flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{job.department}</span>
              </span>
            )}
            {job.department && job.location && (
              <span className="text-gray-600">•</span>
            )}
            {job.location && (
              <span className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{job.location}</span>
              </span>
            )}
          </div>

          {/* Description with single line clamp and ellipsis */}
          <div className="mb-4 flex-1">
            <p 
              className="text-gray-400 text-sm leading-relaxed line-clamp-1"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {job.description}
            </p>
          </div>

          {/* Tags and Status Badge on same line */}
          <div className="mt-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs bg-amber-500/10 text-amber-400 px-3 py-1.5 rounded-full font-medium">
                {job.jobType || 'Full-time'}
              </span>
              {job.level && (
                <span className="text-xs bg-white/5 text-gray-300 px-3 py-1.5 rounded-full font-medium">
                  {job.level}
                </span>
              )}
            </div>
            
            {/* Status Badge - Inline with tags */}
            {(() => {
              if (jobStatus.applied && jobStatus.type === 'application') {
                const app = jobStatus.data as Application;
                if (app.status === 'accepted') {
                  return (
                    <span className="text-[10px] bg-gradient-to-r from-emerald-500/80 to-emerald-600/85 text-white px-2 py-1 rounded-full font-bold shadow-sm">
                      ACCEPTED
                    </span>
                  );
                } else if (app.status === 'rejected') {
                  return (
                    <span className="text-[10px] bg-gradient-to-r from-red-500/80 to-red-600/85 text-white px-2 py-1 rounded-full font-bold shadow-sm">
                      NOT SELECTED
                    </span>
                  );
                } else if (app.status === 'human_pending' || app.status === 'ai_pending') {
                  const interviewSession = interviewSessions.find(s => s.jobId === app.jobId);
                  if (interviewSession?.status === 'in_progress') {
                    return (
                      <span className="text-[10px] bg-gradient-to-r from-amber-500/85 to-amber-600/90 text-white px-2 py-1 rounded-full font-bold shadow-sm">
                        IN PROGRESS
                      </span>
                    );
                  } else if (interviewSession?.status === 'completed') {
                    return (
                      <span className="text-[10px] bg-gradient-to-r from-green-500/80 to-green-600/85 text-white px-2 py-1 rounded-full font-bold shadow-sm">
                        COMPLETED
                      </span>
                    );
                  } else {
                    return (
                      <span className="text-[10px] bg-gradient-to-r from-blue-400/80 to-blue-600/85 text-white px-2 py-1 rounded-full font-bold shadow-sm">
                        READY
                      </span>
                    );
                  }
                } else if (app.status === 'reviewed' || app.status === 'interviewed') {
                  return (
                    <span className="text-[10px] bg-gradient-to-r from-orange-400/80 to-orange-500/85 text-white px-2 py-1 rounded-full font-bold shadow-sm">
                      UNDER REVIEW
                    </span>
                  );
                } else {
                  return (
                    <span className="text-[10px] bg-gradient-to-r from-cyan-500/80 to-cyan-600/85 text-white px-2 py-1 rounded-full font-bold shadow-sm">
                      APPLIED
                    </span>
                  );
                }
              } else if (jobStatus.applied && jobStatus.type === 'interview') {
                const session = jobStatus.data as InterviewSession;
                if (session.status === 'in_progress') {
                  return (
                    <span className="text-[10px] bg-gradient-to-r from-amber-500/85 to-amber-600/90 text-white px-2 py-1 rounded-full font-bold shadow-sm">
                      IN PROGRESS
                    </span>
                  );
                } else {
                  return (
                    <span className="text-[10px] bg-gradient-to-r from-cyan-500/80 to-cyan-600/85 text-white px-2 py-1 rounded-full font-bold shadow-sm">
                      APPLIED
                    </span>
                  );
                }
              } else {
                return null;
              }
            })()}
          </div>
        </div>
      </motion.div>
    );
  };

  // Standardized Stats Card Component - Redesigned with better mobile support
  const StatsCard = ({
    icon: Icon,
    iconGradient,
    value,
    title,
    subtitle,
    animationDelay = 0
  }: {
    icon: any;
    iconGradient: { from: string; to: string; bg: string };
    value: string | number;
    title: string;
    subtitle?: string;
    animationDelay?: number;
  }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: animationDelay, type: "spring", stiffness: 100 }}
        whileHover={{ y: -4, transition: { duration: 0.2 } }}
        className="relative bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl md:rounded-2xl p-4 md:p-6 shadow-xl hover:shadow-2xl hover:bg-white/[0.07] hover:border-white/20 transition-all duration-300 group overflow-hidden"
      >
        {/* Background gradient effect */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-amber-500/5 to-orange-500/5" />

        <div className="relative z-10">
          {/* Mobile: Clean minimal layout with colored accent */}
          <div className="flex md:hidden flex-col items-center text-center gap-2">
            {/* Colored accent bar - more opaque */}
            <div className={`h-1.5 w-16 rounded-full ${iconGradient.from.includes('amber') ? 'bg-gradient-to-r from-amber-400 to-orange-400' : iconGradient.from.includes('orange') ? 'bg-gradient-to-r from-orange-400 to-rose-400' : 'bg-gradient-to-r from-green-400 to-emerald-400'} shadow-lg`} />

            {/* Number and title stacked */}
            <div className="w-full">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: animationDelay + 0.2, type: "spring", bounce: 0.4 }}
                className={`text-4xl font-bold mb-1 ${iconGradient.from.includes('amber') ? 'bg-gradient-to-r from-amber-300 to-orange-300' : iconGradient.from.includes('orange') ? 'bg-gradient-to-r from-orange-300 to-rose-300' : 'bg-gradient-to-r from-green-300 to-emerald-300'} bg-clip-text text-transparent`}
              >
                {value}
              </motion.div>
              <p className="text-gray-300 font-medium text-sm leading-snug" title={title}>{title}</p>
            </div>
          </div>

          {/* Desktop: Horizontal layout with icon */}
          <div className="hidden md:block">
            <div className="flex items-center justify-between mb-4">
              <div className={`${iconGradient.bg} backdrop-blur-xl rounded-2xl p-3 border ${iconGradient.from.includes('amber') ? 'border-amber-500/20' : iconGradient.from.includes('orange') ? 'border-orange-500/20' : 'border-green-500/20'} shadow-lg`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <motion.span
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: animationDelay + 0.2, type: "spring", bounce: 0.4 }}
                className="text-4xl font-bold text-white"
              >
                {value}
              </motion.span>
            </div>
            <p className="text-gray-200 font-semibold text-base" title={title}>{title}</p>
          </div>
        </div>
      </motion.div>
    );
  };

  // Get application status for a job
  const getJobApplicationStatus = (jobId: string) => {
    // Check applications first
    const application = applications.find(app => app.jobId === jobId);
    if (application) {
      return {
        applied: true,
        status: application.status,
        type: 'application',
        data: application
      };
    }

    // Check interview sessions
    const session = interviewSessions.find(session => session.jobId === jobId);
    if (session) {
      return {
        applied: true,
        status: session.status,
        type: 'interview',
        data: session
      };
    }

    return {
      applied: false,
      status: null,
      type: null,
      data: null
    };
  };

  // Enhanced status determination based on WORKFLOW_CHARTS.md Chart D
  const getDetailedApplicationStatus = (application: Application) => {
    // Find corresponding interview session
    const interviewSession = interviewSessions.find(
      session => session.jobId === application.jobId
    );

    // Implement Chart D logic: combine job_applications.status + interview_session.status
    switch (application.status) {
      case 'ai_pending':
        if (!interviewSession) {
          return {
            message: 'Ready',
            description: 'Your interview is ready to start.',
            color: 'bg-blue-500/20 text-blue-400',
            actionText: 'Start Interview',
            actionColor: 'bg-blue-600 hover:bg-blue-700',
            canTakeAction: true,
            actionType: 'start_interview'
          };
        }

        switch (interviewSession.status) {
          case 'in_progress':
            return {
              message: 'In Progress',
              description: 'Continue where you left off.',
              color: 'bg-orange-500/20 text-orange-400',
              actionText: 'Continue Interview',
              actionColor: 'bg-orange-600 hover:bg-orange-700',
              canTakeAction: true,
              actionType: 'continue_interview',
              interviewUrl: interviewSession.interviewUrl
            };

          case 'completed':
            return {
              message: 'Under Review',
              description: 'We are reviewing your responses.',
              color: 'bg-green-500/20 text-green-400',
              actionText: 'Under Review',
              actionColor: 'bg-gray-600',
              canTakeAction: false
            };

          case 'cancelled':
            return {
              message: 'Not Started',
              description: 'You cancelled this interview. Ready to try again?',
              color: 'bg-blue-500/20 text-blue-400',
              actionText: 'Start Interview',
              actionColor: 'bg-blue-600 hover:bg-blue-700',
              canTakeAction: true,
              actionType: 'start_interview'
            };

          default:
            return {
              message: 'Interview Available',
              description: 'Your interview is ready to start.',
              color: 'bg-blue-500/20 text-blue-400',
              actionText: 'Start Interview',
              actionColor: 'bg-blue-600 hover:bg-blue-700',
              canTakeAction: true,
              actionType: 'start_interview'
            };
        }

      case 'human_pending':
        if (!interviewSession) {
          return {
            message: 'Pending',
            description: 'Click Start Interview when ready',
            color: 'bg-blue-500/20 text-blue-400',
            actionText: 'Start Interview',
            actionColor: 'bg-blue-600 hover:bg-blue-700',
            canTakeAction: true,
            actionType: 'start_interview'
          };
        }

        switch (interviewSession.status) {
          case 'in_progress':
            return {
              message: 'In Progress',
              description: 'Continue where you left off',
              color: 'bg-orange-500/20 text-orange-400',
              actionText: 'Continue Interview',
              actionColor: 'bg-orange-600 hover:bg-orange-700',
              canTakeAction: true,
              actionType: 'continue_interview',
              interviewUrl: interviewSession.interviewUrl
            };

          case 'completed':
            return {
              message: 'Under Review',
              description: 'Team is reviewing your assessment',
              color: 'bg-green-500/20 text-green-400',
              actionText: 'Under Review',
              actionColor: 'bg-gray-600',
              canTakeAction: false
            };

          case 'abandoned':
            return {
              message: 'Incomplete',
              description: 'Click to restart your interview',
              color: 'bg-yellow-500/20 text-yellow-400',
              actionText: 'Restart Interview',
              actionColor: 'bg-yellow-600 hover:bg-yellow-700',
              canTakeAction: true,
              actionType: 'restart_interview',
              interviewUrl: interviewSession.interviewUrl
            };

          default:
            return {
              message: 'Pending',
              description: 'Click Start Interview when ready',
              color: 'bg-blue-500/20 text-blue-400',
              actionText: 'Start Interview',
              actionColor: 'bg-blue-600 hover:bg-blue-700',
              canTakeAction: true,
              actionType: 'start_interview'
            };
        }

      case 'reviewed':
        return {
          message: 'Reviewing',
          description: 'We are reviewing your interview results',
          color: 'bg-purple-500/20 text-purple-400',
          actionText: 'Review in Progress',
          actionColor: 'bg-gray-600',
          canTakeAction: false
        };

      case 'interviewed':
        return {
          message: 'Processing',
          description: 'Interviews are in progress',
          color: 'bg-indigo-500/20 text-indigo-400',
          actionText: 'Interviews Ongoing',
          actionColor: 'bg-gray-600',
          canTakeAction: false
        };

      case 'accepted':
        return {
          message: 'Accepted',
          description: 'Your application has been accepted',
          color: 'bg-green-500/20 text-green-400',
          actionText: 'Check Email',
          actionColor: 'bg-green-600 hover:bg-green-700',
          canTakeAction: true,
          actionType: 'view_details'
        };

      case 'rejected':
        // Check if rejection happened after AI interview or before
        const hasCompletedInterview = interviewSession?.status === 'completed';
        if (hasCompletedInterview) {
          return {
            message: 'Rejected',
            description: 'Thank you for completing our interview',
            color: 'bg-red-500/20 text-red-400',
            actionText: 'View Other Jobs',
            actionColor: 'bg-purple-600 hover:bg-purple-700',
            canTakeAction: true,
            actionType: 'view_jobs'
          };
        } else {
          return {
            message: 'Rejected',
            description: 'Thank you for your interest',
            color: 'bg-red-500/20 text-red-400',
            actionText: 'View Other Jobs',
            actionColor: 'bg-purple-600 hover:bg-purple-700',
            canTakeAction: true,
            actionType: 'view_jobs'
          };
        }

      default:
        return {
          message: 'Unknown',
          description: 'Please contact support',
          color: 'bg-gray-500/20 text-gray-400',
          actionText: 'Contact Support',
          actionColor: 'bg-gray-600',
          canTakeAction: false
        };
    }
  };

  // Handle application detail modal open
  const handleOpenApplicationModal = (app: Application) => {
    setSelectedApplication(app);
    setShowApplicationModal(true);
  };

  // Handle job card click - show modal for applied jobs, navigate to detail page for non-applied jobs
  const handleJobCardClick = (job: Job) => {
    const jobStatus = getJobApplicationStatus(job.id);

    // If user has applied to this job (either through application or interview session), show status modal
    if (jobStatus.applied) {
      setSelectedJob(job);
      setShowJobResultModal(true);
    } else {
      // If user hasn't applied, navigate to job detail page for application
      navigate(`/careers/${job.id}`);
    }
  };

  const handleStartInterview = async (jobIdOrLink: string) => {
    // If it's a link (starts with /), navigate directly
    if (jobIdOrLink.startsWith('/')) {
      navigate(jobIdOrLink);
      return;
    }

    // Otherwise, it's a job ID - create interview session first
    try {
      const response = await apiClient.post('/interviews/', {
        jobId: jobIdOrLink,
        status: 'in_progress'
      });

      if (response.data.success) {
        const sessionId = response.data.data.id;
        const interviewUrl = response.data.data.interviewUrl;
        navigate(interviewUrl);

        // Refresh dashboard data to show updated state
        loadDashboardData();
      }
    } catch (error) {
      // Error handling
    }
  };

  // DEVELOPMENT ONLY: Reset all user applications
  const handleResetAllApplications = async () => {
    if (!isDevelopmentMode) {
      return;
    }

    // Count unique applications (not separate sessions)
    const totalApplications = applications.length;

    if (totalApplications === 0) {
      alert('🤷‍♀️ No applications to reset!');
      return;
    }

    if (!confirm(`🧪 DEV MODE: Are you sure you want to RESET ALL ${totalApplications} application(s)?\n\nThis will delete:\n- ${applications.length} job application(s)\n- ${interviewSessions.length} interview session(s)\n- Related data\n\nYou'll be able to reapply to all jobs fresh.`)) {
      return;
    }

    try {
      setLoading(true);

      const response = await apiClient.delete('/auth/dev/reset-applications');

      if (response.data.success) {
        // No localStorage to clear since we removed fallback systems

        // Refresh dashboard data to reflect changes
        await loadDashboardData();

        alert(`✅ ${response.data.message}\n\nDashboard refreshed! You can now reapply to any job.`);
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`❌ Failed to reset applications: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (updatedData: any) => {
    setUpdating(true);
    try {
      // Call the backend API to update the profile
      const response = await apiClient.patch('/auth/me', {
        name: updatedData.name,
        phone: updatedData.phone,
        title: updatedData.title,
        location: updatedData.location,
        about: updatedData.about,
        skills: updatedData.skills,
        education: updatedData.education
      });

      // Update the local state with the response data from server
      const serverData = response.data?.data || {};

      const updatedProfile: Profile = {
        ...profile!,
        // Use server response data to ensure consistency
        name: serverData.name || updatedData.name,
        phone: serverData.phone || updatedData.phone,
        title: serverData.title || updatedData.title,
        location: serverData.location || updatedData.location,
        about: serverData.about || updatedData.about,
        skills: serverData.skills || updatedData.skills,
        education: serverData.education || updatedData.education
      };

      setProfile(updatedProfile);
      setShowUpdateModal(false);

      alert('Profile updated successfully!');

      // Reload dashboard data to get fresh data from server
      await loadDashboardData();

    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to update profile';
      alert(`Failed to update profile: ${errorMessage}`);
    } finally {
      setUpdating(false);
    }
  };

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black overflow-x-hidden">
      {/* Morphing Header with scroll transitions */}
      <AnimateHeader
        logoSrcLeft="/assets/images/branding/Website-Icon.png"
        navItems={[
          { label: "Overview", href: "#overview" },
          { label: "Positions", href: "#jobs" },
          { label: "Profile", href: "#profile" }
        ]}
        activeTab={
          activeTab === 'overview' ? 'Overview' :
          activeTab === 'jobs' ? 'Positions' :
          'Profile'
        }
        onTabChange={(label: string) => {
          if (label === "Overview") setActiveTab('overview');
          else if (label === "Positions") setActiveTab('jobs');
          else if (label === "Profile") setActiveTab('profile');
        }}
        mergeDistance={260}
        // Profile menu props
        profile={profile}
        user={user}
        loading={loading}
        setActiveTab={(tab) => setActiveTab(tab as 'overview' | 'jobs' | 'profile')}
        logout={logout}
        isDevelopmentMode={isDevelopmentMode}
        handleResetAllApplications={handleResetAllApplications}
      />

      {/* Content with proper spacing for unified header */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-32 md:pt-36 pb-12 sm:pb-16 md:pb-24 will-change-transform">
        <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="transform-gpu"
          >
            <div className="grid grid-cols-3 gap-2 md:gap-6 will-change-transform">
            {/* Stats Cards */}
            <StatsCard
              icon={Calendar}
              iconGradient={{
                from: 'from-amber-500/20',
                to: 'to-orange-500/20',
                bg: 'bg-gradient-to-br from-amber-500/20 to-orange-500/20'
              }}
              value={(() => {
                const activeSessionsCount = interviewSessions.filter(session =>
                  session.status === 'in_progress' || session.status === 'cancelled'
                ).length;
                const readyToStartApps = applications.filter(app =>
                  app.status === 'ai_pending' && !interviewSessions.some(session => session.jobId === app.jobId)
                ).length;
                return activeSessionsCount + readyToStartApps;
              })()}
              title="Active Interviews"
              subtitle="Ready to continue"
              animationDelay={0}
            />

            <StatsCard
              icon={Search}
              iconGradient={{
                from: 'from-orange-500/20',
                to: 'to-rose-500/20',
                bg: 'bg-gradient-to-br from-orange-500/20 to-rose-500/20'
              }}
              value={jobs.length}
              title="Available Roles"
              subtitle="Explore opportunities"
              animationDelay={0.1}
            />

            <StatsCard
              icon={CheckCircle}
              iconGradient={{
                from: 'from-green-500/20',
                to: 'to-teal-500/20',
                bg: 'bg-gradient-to-br from-green-500/20 to-teal-500/20'
              }}
              value={applications.filter(app => {
                const hasCompletedSession = interviewSessions.some(session =>
                  String(session.jobId) === String(app.jobId) && session.status === 'completed'
                );
                return ['interviewed', 'reviewed', 'accepted', 'rejected'].includes(app.status) ||
                       (app.status === 'human_pending' && hasCompletedSession);
              }).length}
              title="Completed Applications"
              subtitle="Track progress"
              animationDelay={0.2}
            />
            </div>

            {/* Dynamic Section Sorting Logic */}
            {(() => {
              // 1. Calculate latest dates and item counts for each section
              const activeInterviewsItems = [
                ...interviewSessions.filter(session => session.status === 'in_progress'),
                ...applications.filter(app =>
                  app.status === 'ai_pending' && !interviewSessions.some(session => session.jobId === app.jobId)
                )
              ];

              const updatesItems = applications.filter(app => {
                const hasCompletedSession = interviewSessions.some(session =>
                  String(session.jobId) === String(app.jobId) && session.status === 'completed'
                );
                return app.status === 'human_pending' && hasCompletedSession;
              });

              const myApplicationsItems = applications.filter(app =>
                ['reviewed', 'interviewed', 'accepted', 'rejected'].includes(app.status)
              );

              // 2. Get latest dates for each section
              const getLatestDate = (items: any[], dateField: string) => {
                if (items.length === 0) return null;
                const dates = items.map(item => new Date(item[dateField] || item.appliedAt || item.started_at));
                return new Date(Math.max(...dates.map(d => d.getTime())));
              };

              const activeInterviewsLatest = getLatestDate(activeInterviewsItems, 'started_at');
              const updatesLatest = getLatestDate(updatesItems.map(app => {
                const session = interviewSessions.find(s => String(s.jobId) === String(app.jobId) && s.status === 'completed');
                return { ...app, finished_at: session?.finished_at };
              }), 'finished_at');
              const myApplicationsLatest = getLatestDate(myApplicationsItems, 'appliedAt');

              // 3. Create section metadata
              const sections = [
                {
                  name: 'active',
                  date: activeInterviewsLatest,
                  hasItems: activeInterviewsItems.length > 0,
                  priority: 1
                },
                {
                  name: 'updates',
                  date: updatesLatest,
                  hasItems: updatesItems.length > 0,
                  priority: 2
                },
                {
                  name: 'myApplications',
                  date: myApplicationsLatest,
                  hasItems: myApplicationsItems.length > 0,
                  priority: 3
                }
              ];

              // 4. Sort sections
              const sortedSections = sections.sort((a, b) => {
                // Active Interviews always first if it has items
                if (a.name === 'active' && a.hasItems) return -1;
                if (b.name === 'active' && b.hasItems) return 1;

                // Empty sections go to bottom
                if (!a.hasItems && b.hasItems) return 1;
                if (a.hasItems && !b.hasItems) return -1;

                // Both empty: maintain original order
                if (!a.hasItems && !b.hasItems) return a.priority - b.priority;

                // Both have items: sort by latest date (newest first)
                if (a.date && b.date) {
                  return b.date.getTime() - a.date.getTime();
                }

                return a.priority - b.priority;
              });

              // 5. Render sections in sorted order
              return sortedSections.map(section => {
                if (section.name === 'active') {
                  return (
                    <div key="active-interviews" className="mt-12">

            {/* Active Interviews Section with consistent Apple-style header */}
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-lg md:text-2xl font-semibold text-white flex items-center gap-2 md:gap-3">
                  <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 backdrop-blur-xl rounded-2xl p-2 border border-amber-500/20 shadow-lg">
                    <Activity className="w-4 md:w-5 h-4 md:h-5 text-white" />
                  </div>
                  <span className="truncate">Active Interviews</span>
                </h2>
                <span className="px-3.5 py-1.5 bg-amber-500/20 backdrop-blur-sm rounded-full text-amber-300 text-sm font-semibold">
                  {(() => {
                    const activeSessionsCount = interviewSessions.filter(session =>
                      session.status === 'in_progress' || session.status === 'cancelled'
                    ).length;
                    const readyToStartApps = applications.filter(app =>
                      app.status === 'ai_pending' && !interviewSessions.some(session => session.jobId === app.jobId)
                    ).length;
                    return activeSessionsCount + readyToStartApps;
                  })()}
                </span>
              </div>
              <div className="space-y-4">
                {/* Show in-progress and cancelled interview sessions */}
                {interviewSessions.filter(session => session.status === 'in_progress' || session.status === 'cancelled').map((session, index) => {
                  // Create consistent statusInfo for interview sessions
                  const statusInfo = {
                    message: session.status === 'cancelled' ? 'Not Started' : 'In Progress'
                  };
                  
                  return (
                    <ApplicationCard
                      key={session.id}
                      title={session.jobTitle}
                      department={session.department || 'Unknown Department'}
                      dateText={getStatusBasedDateText(statusInfo.message, session.started_at, session.started_at)}
                      statusInfo={statusInfo}
                      onClick={() => {
                        const app = applications.find(a => a.jobId === session.jobId);
                        if (app) handleOpenApplicationModal(app);
                      }}
                      animationDelay={index}
                    />
                  );
                })}

                {/* Show ai_pending applications ready to start */}
                {applications.filter(app =>
                  app.status === 'ai_pending' && !interviewSessions.some(session => session.jobId === app.jobId)
                ).map((app, index) => {
                  const statusInfo = getDetailedApplicationStatus(app);
                  const sessionOffset = interviewSessions.filter(session => session.status === 'in_progress').length;

                  return (
                    <ApplicationCard
                      key={app.id}
                      title={app.jobTitle}
                      department={app.department}
                      dateText={getStatusBasedDateText(statusInfo.message, app.appliedAt)}
                      statusInfo={statusInfo}
                      onClick={() => handleOpenApplicationModal(app)}
                      animationDelay={sessionOffset + index}
                    />
                  );
                })}

                {/* Show empty state only if no active interviews at all */}
                {interviewSessions.filter(session => session.status === 'in_progress').length === 0 &&
                 applications.filter(app => app.status === 'ai_pending' && !interviewSessions.some(session => session.jobId === app.jobId)).length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <Calendar className="w-8 md:w-12 h-8 md:h-12 mx-auto mb-4 opacity-50" />
                    <p>No active interviews at this time</p>
                  </div>
                )}
              </div>
            </div>
                    </div>
                  );
                } else if (section.name === 'updates') {
                  return (
                    <div key="updates" className="mt-12">

            {/* Updates Section with Apple-style header */}
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-lg md:text-2xl font-semibold text-white flex items-center gap-2 md:gap-3">
                  <div className="bg-gradient-to-br from-green-500/20 to-teal-500/20 backdrop-blur-xl rounded-2xl p-2 border border-green-500/20 shadow-lg">
                    <TrendingUp className="w-4 md:w-5 h-4 md:h-5 text-white" />
                  </div>
                  <span className="truncate">Updates</span>
                </h2>
                <span className="px-3.5 py-1.5 bg-green-500/20 backdrop-blur-sm rounded-full text-green-300 text-sm font-semibold">
                    {applications.filter(app => {
                      const hasCompletedSession = interviewSessions.some(session =>
                        String(session.jobId) === String(app.jobId) && session.status === 'completed'
                      );
                      return app.status === 'human_pending' && hasCompletedSession;
                    }).length}
                  </span>
              </div>
              <div className="space-y-4">
                {applications.filter(app => {
                  // Show only completed AI interviews awaiting admin review
                  const hasCompletedSession = interviewSessions.some(session =>
                    String(session.jobId) === String(app.jobId) && session.status === 'completed'
                  );
                  return app.status === 'human_pending' && hasCompletedSession;
                }).map((app, index) => {
                  const statusInfo = getDetailedApplicationStatus(app);
                  // Find the completed session for this application
                  const completedSession = interviewSessions.find(session => 
                    String(session.jobId) === String(app.jobId) && session.status === 'completed'
                  );

                  return (
                    <ApplicationCard
                      key={app.id}
                      title={app.jobTitle}
                      department={app.department}
                      dateText={getStatusBasedDateText(
                        statusInfo.message, 
                        app.appliedAt,
                        undefined,
                        completedSession?.finished_at
                      )}
                      statusInfo={statusInfo}
                      onClick={() => handleOpenApplicationModal(app)}
                      animationDelay={index}
                    />
                  );
                })}

                {applications.filter(app => {
                  const hasCompletedSession = interviewSessions.some(session =>
                    String(session.jobId) === String(app.jobId) && session.status === 'completed'
                  );
                  return app.status === 'human_pending' && hasCompletedSession;
                }).length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <TrendingUp className="w-8 md:w-12 h-8 md:h-12 mx-auto mb-4 opacity-50" />
                    <p>All caught up</p>
                    <p className="text-sm mt-2">New updates will appear here after completing interviews</p>
                  </div>
                )}
              </div>
            </div>
                    </div>
                  );
                } else if (section.name === 'myApplications') {
                  return (
                    <div key="my-applications" className="mt-12">

            {/* My Applications Section with Apple-style header */}
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-lg md:text-2xl font-semibold text-white flex items-center gap-2 md:gap-3">
                  <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-xl rounded-2xl p-2 border border-blue-500/20 shadow-lg">
                    <FileText className="w-4 md:w-5 h-4 md:h-5 text-white" />
                  </div>
                  <span className="truncate">My Applications</span>
                </h2>
                <span className="px-3.5 py-1.5 bg-blue-500/20 backdrop-blur-sm rounded-full text-blue-300 text-sm font-semibold">
                    {applications.filter(app =>
                      ['reviewed', 'interviewed', 'accepted', 'rejected'].includes(app.status)
                    ).length}
                  </span>
              </div>
              <div className="space-y-4">
                {applications.filter(app =>
                  ['reviewed', 'interviewed', 'accepted', 'rejected'].includes(app.status)
                ).map((app, index) => {
                  const statusInfo = getDetailedApplicationStatus(app);
                  // Find associated session for completion dates
                  const associatedSession = interviewSessions.find(session => 
                    String(session.jobId) === String(app.jobId)
                  );

                  return (
                    <ApplicationCard
                      key={app.id}
                      title={app.jobTitle}
                      department={app.department}
                      dateText={getStatusBasedDateText(
                        statusInfo.message, 
                        app.appliedAt,
                        associatedSession?.started_at,
                        associatedSession?.finished_at
                      )}
                      statusInfo={statusInfo}
                      onClick={() => handleOpenApplicationModal(app)}
                      animationDelay={index}
                    />
                  );
                })}

                {applications.filter(app =>
                  ['reviewed', 'interviewed', 'accepted', 'rejected'].includes(app.status)
                ).length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <FileText className="w-8 md:w-12 h-8 md:h-12 mx-auto mb-4 opacity-50" />
                    <p>No applications to display</p>
                    <p className="text-sm mt-2">Your application history will appear here as you progress through interviews</p>
                  </div>
                )}
              </div>
            </div>
                    </div>
                  );
                }
                return null;
              });
            })()}
          </motion.div>
        )}

        {activeTab === 'jobs' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {jobs.map((job, index) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onClick={handleJobCardClick}
                  animationDelay={index}
                />
              ))}
            </div>

            {jobs.length === 0 && (
              <div className="text-center py-20">
                <Search className="w-10 md:w-16 h-10 md:h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No open positions available</p>
                <p className="text-gray-500 text-sm mt-2">Check back later for new opportunities</p>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/30 backdrop-blur-xl border border-gray-800/50 rounded-2xl p-5 md:p-10 shadow-xl shadow-black/50"
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="flex flex-col md:flex-row items-center md:items-center md:justify-between gap-3 md:gap-8 mb-6 md:mb-10 pb-4 md:pb-10 border-b border-gray-800/50"
              >
                <div className="flex flex-col md:flex-row items-center gap-3 md:gap-8 w-full md:w-auto">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.3 }}
                    className="relative"
                  >
                    <div className="w-16 md:w-24 h-16 md:h-24 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center border border-white/20 shadow-2xl">
                      <User className="w-8 md:w-12 h-8 md:h-12 text-white" />
                    </div>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.5 }}
                      className="absolute -bottom-1 -right-1 w-6 md:w-8 h-6 md:h-8 bg-green-500 rounded-full flex items-center justify-center border-2 md:border-4 border-gray-900"
                    >
                      <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-white" />
                    </motion.div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="flex-1 text-center md:text-left"
                  >
                    <h2 className="text-xl md:text-3xl font-semibold text-white mb-1 md:mb-2">{profile?.name}</h2>
                    <p className="text-gray-400 text-sm md:text-base">{profile?.email}</p>
                    {profile?.title && (
                      <p className="text-amber-400 font-medium text-sm md:text-base mt-1 md:mt-2">{profile.title}</p>
                    )}
                  </motion.div>
                </div>
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowUpdateModal(true)}
                  disabled={updating}
                  className="w-full md:w-auto px-4 md:px-6 py-2 md:py-3 bg-white/10 backdrop-blur-sm text-white rounded-xl text-sm md:text-base font-medium border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Settings className="w-4 h-4 hidden md:inline" />
                  {updating ? 'Updating...' : 'Edit Profile'}
                </motion.button>
              </motion.div>

              <div className="space-y-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gray-800/30"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
                    <div className="text-center md:text-left w-full md:w-auto">
                      <div className="flex flex-col md:flex-row items-center gap-4">
                        <div className={`p-3 rounded-xl ${profile?.cvFileName ? 'bg-green-500/10 border border-green-500/20' : 'bg-gray-700/30 border border-gray-700/50'}`}>
                          <FileText className={`w-5 md:w-6 h-5 md:h-6 ${profile?.cvFileName ? 'text-green-400' : 'text-gray-500'}`} />
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium">{profile?.cvFileName || 'No CV uploaded'}</p>
                          {profile?.cvFileName && <p className="text-gray-500 text-sm mt-1">Uploaded and verified</p>}
                        </div>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate('/register')}
                      className="w-full md:w-auto px-4 md:px-5 py-2 md:py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-medium shadow-lg hover:shadow-amber-500/25 transition-all duration-300"
                    >
                      Upload New CV
                    </motion.button>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="grid grid-cols-1 gap-4 md:gap-6"
                  style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}
                >
                  {profile?.location && (
                    <div className="bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gray-800/30 text-center md:text-left">
                      <label className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-3 block">Location</label>
                      <p className="text-white text-sm md:text-base font-medium">{profile.location}</p>
                    </div>
                  )}

                  {profile?.phone && (
                    <div className="bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gray-800/30 text-center md:text-left">
                      <label className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-3 block">Phone</label>
                      <p className="text-white text-sm md:text-base font-medium">{profile.phone}</p>
                    </div>
                  )}
                </motion.div>

                {profile?.skills && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gray-800/30 text-center md:text-left"
                  >
                    <label className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-4 block">Skills & Expertise</label>
                    <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                      {Array.isArray(profile.skills) ? profile.skills.slice(0, 10).map((skill, index) => (
                        <motion.span
                          key={index}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.05 }}
                          className="px-4 py-2 bg-gradient-to-r from-primary-500/20 to-primary-600/20 text-primary-300 rounded-xl text-sm font-medium border border-primary-500/20"
                        >
                          {typeof skill === 'string' ? skill : skill.name || 'Skill'}
                        </motion.span>
                      )) : (
                        <span className="text-white">Skills available</span>
                      )}
                    </div>
                  </motion.div>
                )}

                {profile?.experience && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gray-800/30 text-center md:text-left"
                  >
                    <label className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-4 block">Experience</label>
                    <div className="space-y-4">
                      {Array.isArray(profile.experience) ? profile.experience.slice(0, 3).map((exp, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ x: 4, transition: { duration: 0.2 } }}
                          className="relative bg-gradient-to-r from-gray-800/40 to-gray-800/20 rounded-xl border border-gray-700/30 group hover:border-primary-400/30 hover:shadow-lg hover:shadow-primary-400/10 transition-all duration-300 overflow-hidden"
                        >
                          {/* Yellow accent border centered on card border */}
                          <div className="absolute -left-[2px] top-3 bottom-3 w-1 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full transition-all duration-300 group-hover:w-1.5 group-hover:-left-[3px] group-hover:from-primary-300 group-hover:to-primary-500 group-hover:shadow-[0_0_10px_rgba(251,191,36,0.4)]" />
                          <div className="pl-6 pr-4 py-4 transition-all duration-300 group-hover:pl-7">
                            <h4 className="font-semibold text-white text-base mb-1">{exp.position || exp.title}</h4>
                            <p className="text-primary-400 text-sm font-medium mb-1">{exp.company}</p>
                            {(exp.start_date || exp.end_date) && (
                              <p className="text-gray-500 text-sm">
                                {exp.start_date} - {exp.end_date || 'Present'}
                              </p>
                            )}
                          </div>
                        </motion.div>
                      )) : (
                        <span className="text-white text-sm">Experience available</span>
                      )}
                    </div>
                  </motion.div>
                )}

                {profile?.education && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0 }}
                    className="bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gray-800/30 text-center md:text-left"
                  >
                    <label className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-4 block">Education</label>
                    <div className="space-y-4">
                      {Array.isArray(profile.education) ? profile.education.slice(0, 3).map((edu, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ x: 4, transition: { duration: 0.2 } }}
                          className="relative bg-gradient-to-r from-gray-800/40 to-gray-800/20 rounded-xl border border-gray-700/30 group hover:border-primary-400/30 hover:shadow-lg hover:shadow-primary-400/10 transition-all duration-300 overflow-hidden"
                        >
                          {/* Yellow accent border centered on card border */}
                          <div className="absolute -left-[2px] top-3 bottom-3 w-1 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full transition-all duration-300 group-hover:w-1.5 group-hover:-left-[3px] group-hover:from-primary-300 group-hover:to-primary-500 group-hover:shadow-[0_0_10px_rgba(251,191,36,0.4)]" />
                          <div className="pl-6 pr-4 py-4 transition-all duration-300 group-hover:pl-7">
                            <h4 className="font-semibold text-white text-base mb-1">
                              {edu.degree || edu.title || 'Degree'}
                            </h4>
                            {edu.institution && (
                              <p className="text-primary-400 text-sm font-medium mb-1">{edu.institution}</p>
                            )}
                            {(edu.start_date || edu.end_date) && (
                              <p className="text-gray-500 text-sm">
                                {edu.start_date || ''} - {edu.end_date || 'Present'}
                              </p>
                            )}
                            {edu.field_of_study && (
                              <p className="text-gray-500 text-sm mb-1">{edu.field_of_study}</p>
                            )}
                            {edu.gpa && (
                              <p className="text-gray-500 text-sm">GPA: {edu.gpa}</p>
                            )}
                          </div>
                        </motion.div>
                      )) : (
                        <span className="text-white text-sm">Education available</span>
                      )}
                    </div>
                  </motion.div>
                )}

                {profile?.about && (
                  <div>
                    <label className="text-gray-400 text-sm">About</label>
                    <p className="text-white mt-1 text-sm leading-relaxed">{profile.about}</p>
                  </div>
                )}


                {/* Password Change Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gray-800/30"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
                    <div className="text-center md:text-left w-full md:w-auto">
                      <div className="flex flex-col md:flex-row items-center gap-4">
                        <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                          <Lock className="w-5 md:w-6 h-5 md:h-6 text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium">Account Security</p>
                          <p className="text-gray-500 text-sm mt-1">Keep your account secure with a strong password</p>
                        </div>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowPasswordModal(true)}
                      className="w-full md:w-auto px-4 md:px-5 py-2 md:py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl text-sm font-medium shadow-lg hover:shadow-blue-500/25 transition-all duration-300"
                    >
                      Change Password
                    </motion.button>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.3 }}
                  className="bg-gray-800/20 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gray-800/30"
                >
                  <label className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-3 block">Member Since</label>
                  <p className="text-white font-medium">{profile ? formatDate(profile.joinedAt) : 'N/A'}</p>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>
      </div>

      {/* Profile Update Modal */}
      {profile && (
        <ProfileUpdateModal
          isOpen={showUpdateModal}
          onClose={() => setShowUpdateModal(false)}
          onSave={handleUpdateProfile}
          profileData={{
            name: profile.name,
            email: profile.email,
            phone: profile.phone,
            title: profile.title,
            location: profile.location,
            about: profile.about,
            skills: profile.skills,
            experience: profile.experience,
            education: profile.education
          }}
        />
      )}

      {/* Password Update Modal */}
      <PasswordUpdateModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 z-50 p-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300"
            aria-label="Scroll to top"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Application Detail Modal */}
      <ApplicationDetailModal
        isOpen={showApplicationModal}
        onClose={() => {
          setShowApplicationModal(false);
          setSelectedApplication(null);
        }}
        application={selectedApplication}
        interviewSession={selectedApplication ?
          interviewSessions.find(session => session.jobId === selectedApplication.jobId)
          : undefined
        }
        onStartInterview={handleStartInterview}
        onContinueInterview={(interviewUrl) => navigate(interviewUrl)}
        onViewJobs={() => setActiveTab('jobs')}
      />

      {/* Enhanced Job Result Modal - Apple-Inspired Design */}
      {selectedJob && (
        <AnimatePresence>
          {showJobResultModal && (
            <>
              {/* Premium backdrop with enhanced blur */}
              <motion.div
                initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
                animate={{ opacity: 1, backdropFilter: "blur(20px)" }}
                exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
                className="fixed inset-0 bg-black/70 z-50"
                onClick={() => setShowJobResultModal(false)}
              />

              {/* Redesigned Apple-style modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.85, y: 50 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.85, y: 50 }}
                transition={{ type: "spring", bounce: 0.25, duration: 0.5 }}
                className="fixed inset-0 flex items-center justify-center z-50 p-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-white/[0.08] backdrop-blur-3xl border border-white/[0.12] rounded-[28px] p-8 max-w-md w-full mx-auto shadow-2xl relative overflow-hidden">
                  {/* Dynamic status-based gradient overlay */}
                  <div className="absolute inset-0 opacity-30 rounded-[28px] pointer-events-none">
                    {(() => {
                      const jobStatus = getJobApplicationStatus(selectedJob.id);
                      if (jobStatus.applied && jobStatus.type === 'application') {
                        const app = jobStatus.data as Application;
                        switch (app.status) {
                          case 'accepted':
                            return <div className="bg-gradient-to-br from-emerald-400/20 via-green-500/10 to-transparent w-full h-full" />;
                          case 'rejected':
                            return <div className="bg-gradient-to-br from-red-400/20 via-rose-500/10 to-transparent w-full h-full" />;
                          case 'human_pending':
                          case 'ai_pending':
                            const interviewSession = interviewSessions.find(s => s.jobId === app.jobId);
                            if (interviewSession?.status === 'in_progress') {
                              return <div className="bg-gradient-to-br from-amber-400/20 via-orange-500/10 to-transparent w-full h-full" />;
                            } else if (interviewSession?.status === 'completed') {
                              return <div className="bg-gradient-to-br from-blue-400/20 via-indigo-500/10 to-transparent w-full h-full" />;
                            } else {
                              return <div className="bg-gradient-to-br from-blue-400/20 via-cyan-500/10 to-transparent w-full h-full" />;
                            }
                          case 'reviewed':
                          case 'interviewed':
                            return <div className="bg-gradient-to-br from-orange-400/20 via-amber-500/10 to-transparent w-full h-full" />;
                          default:
                            return <div className="bg-gradient-to-br from-purple-400/20 via-indigo-500/10 to-transparent w-full h-full" />;
                        }
                      }
                      return <div className="bg-gradient-to-br from-gray-400/20 via-slate-500/10 to-transparent w-full h-full" />;
                    })()}
                  </div>
                  
                  {/* Content */}
                  <div className="relative z-10">
                    {/* Close button */}
                    <button
                      onClick={() => setShowJobResultModal(false)}
                      className="absolute -top-2 -right-2 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-200 backdrop-blur-sm border border-white/10"
                    >
                      <span className="text-gray-400 hover:text-white text-lg leading-none">×</span>
                    </button>

                    {/* Job Header */}
                    <div className="text-center mb-8">
                      <motion.h3 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-2xl font-bold text-white mb-3 leading-tight"
                        style={{
                          background: 'linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}
                      >
                        {selectedJob.title}
                      </motion.h3>
                      
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex items-center justify-center gap-6 text-gray-300"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                          <span className="text-sm font-medium">{selectedJob.department}</span>
                        </div>
                        {selectedJob.location && (
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                            <span className="text-sm font-medium">{selectedJob.location}</span>
                          </div>
                        )}
                      </motion.div>
                    </div>

                    {/* Status Section */}
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3, type: "spring", bounce: 0.3 }}
                      className="mb-8"
                    >
                      {(() => {
                        const jobStatus = getJobApplicationStatus(selectedJob.id);
                        
                        if (jobStatus.applied && jobStatus.type === 'application') {
                          const app = jobStatus.data as Application;
                          
                          const getStatusConfig = () => {
                            switch (app.status) {
                              case 'accepted':
                                return {
                                  title: 'Application Accepted',
                                  subtitle: 'Congratulations on moving forward',
                                  description: 'Your application has been accepted. You will receive further instructions via email shortly.',
                                  iconBg: 'bg-gradient-to-br from-emerald-400/20 to-green-500/20',
                                  iconColor: 'text-emerald-400',
                                  titleColor: 'text-emerald-300',
                                  borderColor: 'border-emerald-500/30',
                                  glowColor: 'shadow-emerald-500/20',
                                  icon: CheckCircle
                                };
                              case 'rejected':
                                return {
                                  title: 'Application Status',
                                  subtitle: 'Not selected for this position',
                                  description: 'While this opportunity didn\'t work out, we encourage you to explore other positions that match your skills.',
                                  iconBg: 'bg-gradient-to-br from-red-400/20 to-rose-500/20',
                                  iconColor: 'text-red-400',
                                  titleColor: 'text-red-300',
                                  borderColor: 'border-red-500/30',
                                  glowColor: 'shadow-red-500/20',
                                  icon: () => (
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  )
                                };
                              case 'human_pending':
                              case 'ai_pending':
                                const interviewSession = interviewSessions.find(s => s.jobId === app.jobId);
                                if (interviewSession?.status === 'in_progress') {
                                  return {
                                    title: 'Interview Active',
                                    subtitle: 'Continue your assessment',
                                    description: 'Your AI interview is in progress. Return to complete the remaining questions at your convenience.',
                                    iconBg: 'bg-gradient-to-br from-amber-400/20 to-orange-500/20',
                                    iconColor: 'text-amber-400',
                                    titleColor: 'text-amber-300',
                                    borderColor: 'border-amber-500/30',
                                    glowColor: 'shadow-amber-500/20',
                                    icon: Clock
                                  };
                                } else if (interviewSession?.status === 'completed') {
                                  return {
                                    title: 'Interview Complete',
                                    subtitle: 'Under review by our team',
                                    description: 'Thank you for completing the assessment. Our team is reviewing your responses and will update you soon.',
                                    iconBg: 'bg-gradient-to-br from-blue-400/20 to-indigo-500/20',
                                    iconColor: 'text-blue-400',
                                    titleColor: 'text-blue-300',
                                    borderColor: 'border-blue-500/30',
                                    glowColor: 'shadow-blue-500/20',
                                    icon: CheckCircle
                                  };
                                } else {
                                  return {
                                    title: 'Interview Ready',
                                    subtitle: 'Begin your assessment',
                                    description: 'Your application has progressed to the interview stage. Start when you\'re ready to showcase your skills.',
                                    iconBg: 'bg-gradient-to-br from-blue-400/20 to-cyan-500/20',
                                    iconColor: 'text-blue-400',
                                    titleColor: 'text-blue-300',
                                    borderColor: 'border-blue-500/30',
                                    glowColor: 'shadow-blue-500/20',
                                    icon: Activity
                                  };
                                }
                              case 'reviewed':
                              case 'interviewed':
                                return {
                                  title: 'Application Review',
                                  subtitle: 'Being evaluated by our team',
                                  description: 'Your application and interview responses are being carefully reviewed. We\'ll notify you of the next steps.',
                                  iconBg: 'bg-gradient-to-br from-orange-400/20 to-amber-500/20',
                                  iconColor: 'text-orange-400',
                                  titleColor: 'text-orange-300',
                                  borderColor: 'border-orange-500/30',
                                  glowColor: 'shadow-orange-500/20',
                                  icon: Search
                                };
                              default:
                                return {
                                  title: 'Application Submitted',
                                  subtitle: 'Processing your application',
                                  description: 'Your application has been successfully submitted and is being processed by our recruitment team.',
                                  iconBg: 'bg-gradient-to-br from-purple-400/20 to-indigo-500/20',
                                  iconColor: 'text-purple-400',
                                  titleColor: 'text-purple-300',
                                  borderColor: 'border-purple-500/30',
                                  glowColor: 'shadow-purple-500/20',
                                  icon: FileText
                                };
                            }
                          };

                          const config = getStatusConfig();
                          const StatusIcon = config.icon;
                          
                          return (
                            <div className={`relative p-6 rounded-2xl border backdrop-blur-sm ${config.borderColor} bg-white/[0.03] ${config.glowColor} shadow-xl`}>
                              <div className="flex items-start gap-4">
                                <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${config.iconBg} flex items-center justify-center backdrop-blur-sm border border-white/10`}>
                                  <StatusIcon className={`w-6 h-6 ${config.iconColor}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className={`text-lg font-semibold mb-1 ${config.titleColor}`}>
                                    {config.title}
                                  </h4>
                                  <p className="text-gray-400 text-sm font-medium mb-3">
                                    {config.subtitle}
                                  </p>
                                  <p className="text-gray-300 text-sm leading-relaxed">
                                    {config.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        } else {
                          // This should not happen with the updated logic, but keeping as failsafe
                          return (
                            <div className="relative p-6 rounded-2xl border border-gray-500/30 bg-white/[0.03] backdrop-blur-sm shadow-xl">
                              <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-gray-400/20 to-slate-500/20 flex items-center justify-center backdrop-blur-sm border border-white/10">
                                  <Briefcase className="w-6 h-6 text-gray-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-lg font-semibold mb-1 text-gray-300">
                                    Application Status
                                  </h4>
                                  <p className="text-gray-400 text-sm font-medium mb-3">
                                    Unable to determine status
                                  </p>
                                  <p className="text-gray-300 text-sm leading-relaxed">
                                    We couldn't determine your application status for this position. Please contact support if this continues.
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      })()}
                    </motion.div>

                    {/* Action Buttons */}
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="flex gap-3"
                    >
                      <button
                        onClick={() => {
                          setShowJobResultModal(false);
                          setSelectedJob(null);
                          setActiveTab('overview');
                        }}
                        className="flex-1 px-5 py-3.5 bg-white/[0.06] hover:bg-white/[0.1] backdrop-blur-sm rounded-2xl text-gray-300 hover:text-white text-sm font-medium border border-white/[0.08] hover:border-white/20 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
                      >
                        View Overview
                      </button>
                      <button
                        onClick={() => {
                          setShowJobResultModal(false);
                          setSelectedJob(null);
                        }}
                        className="flex-1 px-5 py-3.5 bg-gradient-to-r from-blue-500/90 to-indigo-500/90 hover:from-blue-500 hover:to-indigo-500 text-white rounded-2xl text-sm font-medium shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] backdrop-blur-sm"
                      >
                        Continue Here
                      </button>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default CandidateDashboard;